import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  BRAND_NAME,
  CONTACT_EMAIL,
  CONTACT_PHONE,
  MAILING_ADDRESS,
  SAMPLE_MESSAGE,
} from '../config/brand';

const TermsOfService = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": `Terms of Service - ${BRAND_NAME}`,
    "url": "https://lajdigital.com/terms"
  };

  return (
    <section className="pt-32 pb-24 relative bg-gray-950 min-h-screen">
      <Helmet>
        <title>Terms of Service | {BRAND_NAME}</title>
        <meta name="description" content="Review the Terms of Service for LAJ Digital. Understand the rules, guidelines, and obligations for using our website and services." />
        <link rel="canonical" href="https://lajdigital.com/terms" />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>

      <div className="container-custom max-w-4xl relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-panel p-8 md:p-12 rounded-3xl text-gray-300 font-light space-y-8"
        >
          <div className="border-b border-gray-800 pb-8 mb-8">
            <h1 className="text-3xl md:text-4xl font-display font-bold text-white mb-4">Terms of Service & SMS Terms</h1>
            <p className="text-gray-500">Last Updated: {new Date().toLocaleDateString()}</p>
          </div>

          <div className="space-y-6">
            <h2 className="text-xl font-display font-bold text-white">1. Agreement to Terms</h2>
            <p>
              By accessing our website and utilizing our services, you agree to be bound by these Terms of Service. If you do not agree with all of these terms, you are expressly prohibited from using the site and our services and must discontinue use immediately.
            </p>
            <p>
              These Terms govern your use of this website and our services. Section 2 below describes our optional SMS messaging program. Agreeing to these Terms does not enroll you in SMS messaging and does not constitute consent to receive text messages. SMS consent is collected separately and is entirely voluntary.
            </p>

            <h2 className="text-xl font-display font-bold text-purple-400 p-4 bg-purple-900/10 border-l-2 border-purple-500 rounded-r-xl">
              2. SMS/Text Messaging Program Terms
            </h2>
            <div className="p-6 bg-gray-900/50 rounded-xl space-y-4 border border-gray-800">
              <h3 className="text-lg font-display font-bold text-purple-300">Consent Is Not a Condition of Service</h3>
              <p className="text-gray-200">
                Opting in to receive text messages from {BRAND_NAME} is not required to use this website, contact us, purchase or receive any product or service, create an account, or become a client. You may decline SMS messaging and still access everything we offer. If you have opted in, you may opt out at any time without any effect on your ability to do business with us.
              </p>
              <p>
                By providing your mobile phone number and opting in, you agree to receive text messages from {BRAND_NAME} related to your inquiries, account updates, appointments, and service notifications. Messages are sent by {BRAND_NAME}. We may use automated systems and third-party messaging providers to deliver them on our behalf.
              </p>
              <ul className="list-disc pl-5 space-y-3 text-gray-200">
                <li><strong className="text-white">Program Description:</strong> Messages may include service updates, response to inquiries, appointment reminders, and customer support communications.</li>
                <li><strong className="text-white">Message Frequency:</strong> Message frequency varies based on your interaction with our services.</li>
                <li><strong className="text-white">Pricing Disclosure:</strong> <span className="text-purple-300 font-medium">Message and data rates may apply.</span> Please check with your mobile carrier for details regarding your specific plan.</li>
                <li><strong className="text-white">Opt-Out Instructions:</strong> You can cancel the SMS service at any time. <span className="text-purple-300 font-medium">Reply "STOP" to cancel.</span> After you send the SMS message "STOP" to us, we will send you an SMS message to confirm that you have been unsubscribed. After this, you will no longer receive SMS messages from us.</li>
                <li><strong className="text-white">Help Instructions:</strong> If you are experiencing issues with the messaging program you can <span className="text-purple-300 font-medium">reply with the keyword "HELP" for more assistance</span>, or you can get help directly by emailing us at <a href={`mailto:${CONTACT_EMAIL}`} className="text-purple-300 underline hover:text-purple-200">{CONTACT_EMAIL}</a>.</li>
                <li><strong className="text-white">Carrier Liability:</strong> Carriers are not liable for delayed or undelivered messages.</li>
                <li><strong className="text-white">Sample Message:</strong> <span className="text-gray-300 italic">"{SAMPLE_MESSAGE}"</span></li>
              </ul>
              <p className="text-sm text-gray-400">
                Opting in is entirely optional and separate from these Terms. If you would like to receive text messages, visit our{' '}
                <Link to="/sms-optin" className="text-purple-300 underline hover:text-purple-200">SMS opt-in page</Link>.
              </p>
            </div>

            <h2 className="text-xl font-display font-bold text-white">3. Intellectual Property Rights</h2>
            <p>
              Unless otherwise indicated, the website and services are our proprietary property and all source code, databases, functionality, software, website designs, audio, video, text, photographs, and graphics on the site are owned or controlled by us.
            </p>

            <h2 className="text-xl font-display font-bold text-white">4. Limitation of Liability</h2>
            <p>
              In no event will we or our directors, employees, or agents be liable to you or any third party for any direct, indirect, consequential, exemplary, incidental, special, or punitive damages, including lost profit, lost revenue, loss of data, or other damages arising from your use of the site or our services.
            </p>

            <h2 className="text-xl font-display font-bold text-white">5. Disclaimer</h2>
            <p>
              The site and services are provided on an as-is and as-available basis. You agree that your use of the site and our services will be at your sole risk. To the fullest extent permitted by law, we disclaim all warranties, express or implied, in connection with the site and your use thereof.
            </p>

            <h2 className="text-xl font-display font-bold text-white">6. Contact Us</h2>
            <p>
              If you have questions about these Terms or our SMS messaging program, you can reach us at:
            </p>
            <ul className="list-none space-y-2 text-gray-200">
              <li><strong className="text-white">Email:</strong> <a href={`mailto:${CONTACT_EMAIL}`} className="text-purple-300 underline hover:text-purple-200">{CONTACT_EMAIL}</a></li>
              {CONTACT_PHONE && (
                <li><strong className="text-white">Phone:</strong> <a href={`tel:${CONTACT_PHONE.replace(/[^\d+]/g, '')}`} className="text-purple-300 underline hover:text-purple-200">{CONTACT_PHONE}</a></li>
              )}
              {MAILING_ADDRESS && (
                <li><strong className="text-white">Mailing Address:</strong> {MAILING_ADDRESS}</li>
              )}
            </ul>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TermsOfService;
